import { RELATIONSHIP_OPTIONS } from 'src/consumer/constants'

export const FORM_CONSTANTS = {
  MIN_MOBILE_LENGTH_FOR_SEARCH: 5,
  MIN_MOBILE_LENGTH_FOR_VALIDATION: 10,
  MAX_AGE: 100,
  MIN_AGE: 0,
  AGE_INPUT_MAX_LENGTH: 3,
  SEARCH_DEBOUNCE_TIMEOUT: 1000,
  MENU_MAX_HEIGHT: '350px', // Show ~5 results (70px per result)
  SEARCH_DROPDOWN_WIDTH: '450px', // Reduced width for dropdown
}

export const GRID_CONFIG = {
  xs: 12,
  md: 6
}

export const GENDER_OPTIONS = [
  { label: 'Male', id: 'male' },
  { label: 'Female', id: 'female' },
  { label: 'Other', id: 'nonBinary' },
]

export const FIELD_DEFINITIONS = {
  mobile: {
    label: 'Mobile*',
    type: 'phone',
    required: true,
    placeholder: 'Mobile Number',
  },
  name: {
    label: 'Name*',
    type: 'text',
    required: true,
    placeholder: 'Patient Name',
  },
  gender: {
    label: 'Gender*',
    type: 'radio',
    required: true,
    options: GENDER_OPTIONS,
  },
  age: {
    label: 'Age*',
    type: 'number',
    required: true,
    min: FORM_CONSTANTS.MIN_AGE,
    max: FORM_CONSTANTS.MAX_AGE,
    maxLength: FORM_CONSTANTS.AGE_INPUT_MAX_LENGTH,
  },
  email: {
    label: 'Email',
    type: 'text',
    required: false,
    placeholder: 'Email Address',
  },
  address: {
    label: 'Address',
    type: 'text',
    required: false,
    placeholder: 'Address',
  },
  uhid: {
    label: 'UHID',
    type: 'text',
    required: false,
    placeholder: 'UHID',
  },
  importId: {
    label: 'Import ID',
    type: 'text',
    required: false,
    placeholder: 'Import ID',
  },
  patientNumber: {
    label: 'Patient Number',
    type: 'display',
    required: false,
  },
  language: {
    label: 'Language',
    type: 'select',
    required: false,
  },
  city: {
    label: 'City',
    type: 'text',
    required: false,
    placeholder: 'City',
  },
  pincode: {
    label: 'Pincode',
    type: 'number',
    required: false,
    placeholder: 'Pincode',
  },
  note: {
    label: 'Note',
    type: 'text',
    required: false,
    placeholder: 'Note',
  },
  referredBy: {
    label: 'Referred By',
    type: 'text',
    required: false,
    placeholder: 'Referred By',
  },
  attendants: {
    label: 'Attendants',
    type: 'array',
    required: false,
    defaultItem: {
      name: '',
      primary: false,
      type: RELATIONSHIP_OPTIONS[0].value,
    },
  },
  alternativeMobiles: {
    label: 'Alternative Mobile Numbers',
    type: 'array',
    required: false,
    defaultItem: '',
  },
  dependentRelation: {
    label: 'Relation to',
    type: 'select',
    required: false,
    options: RELATIONSHIP_OPTIONS,
  },
}

export const PATIENT_SEARCH_CONFIG = {
  debounceTimeout: FORM_CONSTANTS.SEARCH_DEBOUNCE_TIMEOUT,
  menuMaxHeight: FORM_CONSTANTS.MENU_MAX_HEIGHT,
  defaultApiSignature: '/api/secure/mentor/patient/list',
  createOptionText: 'Add related patient for this number',
  searchPlaceholder: 'Search Patient...',
  minSearchLength: FORM_CONSTANTS.MIN_MOBILE_LENGTH_FOR_SEARCH,
  menuZIndex: 1000,
}

export const PATIENT_SEARCH_STYLES = {
  control: (base) => ({
    ...base,
    height: 0,
    width: 0,
    visibility: 'hidden',
    overflow: 'hidden',
  }),
  menu: (base, { data }) => ({
    ...base,
    zIndex: 10000,
    width: FORM_CONSTANTS.SEARCH_DROPDOWN_WIDTH,
    maxWidth: FORM_CONSTANTS.SEARCH_DROPDOWN_WIDTH,
    display: data?.length > 0 ? base?.menu : 'none',
    boxShadow: '0 10px 40px rgba(67, 56, 202, 0.15), 0 4px 12px rgba(67, 56, 202, 0.1)',
    border: '1px solid rgba(67, 56, 202, 0.15)',
    borderRadius: '12px',
    overflow: 'hidden',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10000,
  }),
  container: (provided) => ({
    ...provided,
    width: '100%',
    maxWidth: FORM_CONSTANTS.SEARCH_DROPDOWN_WIDTH,
    height: 0,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: FORM_CONSTANTS.MENU_MAX_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '8px',
    scrollBehavior: 'smooth',
    '::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '::-webkit-scrollbar-track': {
      background: 'rgba(241, 245, 249, 0.5)',
      borderRadius: '10px',
    },
    '::-webkit-scrollbar-thumb': {
      background: 'rgba(67, 56, 202, 0.3)',
      borderRadius: '10px',
      transition: 'background 0.2s ease',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(67, 56, 202, 0.5)',
    },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
}

export const FORM_SECTIONS = {
  BASIC_INFO: 'basicInfo',
  MORE_DETAILS: 'moreDetails',
  SEARCH: 'search',
  RELATED_PATIENT: 'relatedPatient',
  ANONYMOUS_PATIENT: 'anonymousPatient',
}

export const DEFAULT_PATIENT_DATA = {
  name: '',
  gender: '',
  age: null,
  mobile: '',
  email: '',
  address: '',
  city: '',
  pincode: '',
  note: '',
  referredBy: '',
  uhId: '',
  attendants: [],
  altMobiles: [],
  defaultCommunicationLanguage: '',
  communicationToAttendant: false,
  importData: {
    importedFromId: '',
  },
}

export const getImportIdLabel = (importIdFieldLabel) => {
  return importIdFieldLabel ? `${importIdFieldLabel} ID` : 'Import ID'
}

export const ATTENDANT_STYLES = {
  input: {
    border: '1px solid #cecece',
    borderRadius: '4px',
  },
  select: {
    border: '1px solid #cecece',
    borderRadius: '4px',
    width: '80px',
  },
}

export const VALIDATION_MESSAGES = {
  REQUIRED: 'Required',
  CHOOSE_ONE: 'Choose one',
  INVALID_FORMAT: 'Invalid format',
  ATTENDANT_NAME_REQUIRED: 'Attendant name is required',
  INVALID_MOBILE: 'Invalid mobile number format',
}

export const CONDITIONAL_DISPLAY_RULES = {
  showSearchWhenMobileLength: (mobile) => (mobile?.length || 0) >= FORM_CONSTANTS.MIN_MOBILE_LENGTH_FOR_SEARCH,
  showAnonymousCheckboxWhen: (patientDetails) =>
    !patientDetails?.mobile && !patientDetails?.email &&
    !(patientDetails?.relatedToExistingPatient && patientDetails?.dependentRelation),
  showCommunicationToAttendantWhen: (attendants) =>
    Array.isArray(attendants) && attendants.length > 0,
  showRelationDropdownWhen: (patientDetails) =>
    patientDetails?.relatedToExistingPatient && patientDetails?.dependentRelation,
}